import { AccountReport } from 'core/models/AccountReport';
import { MovimientoCuenta } from 'core/models/data/MovimientoCuenta';
import { Pageable } from 'shared/table/models/Pageable';
import { createContext, useCallback, useEffect } from 'react';
import { useCoreService } from 'core/services/useCoreService';
import { AccountReportRequest } from 'core/models/AccountReportRequest';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SearchAccountCardTotalsError } from 'core/cuenta/models/errors/SearchAccountCardTotalsError';
import { CARDS_TOTALS_BY_ACCOUNT_PATH, CARDS_TOTALS_PATH } from 'core/tarjeta/routes';
import { HOME_PATH } from 'navigation/routes';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface IAccountDetailsContext {
  closeReport(): void;
  accountData: AccountReport;
  movimientosCuenta: Pageable<MovimientoCuenta>;
  searchParams: URLSearchParams;
}

export const AccountDetailsContext = createContext<IAccountDetailsContext>({
  closeReport: () => {},
  accountData: null,
  movimientosCuenta: null,
  searchParams: null,
});

export const AccountDetailsProvider = ({ children }) => {
  const coreService = useCoreService();
  const [accountData, setAccountDataRequest] = useDataFetcher<AccountReport>(null);
  const { accountNumber } = useParams();
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotifications();
  const { state } = useLocation();
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    let params = {};
    let cardPath = CARDS_TOTALS_PATH;
    if (searchParams.has('mes') && searchParams.has('anio')) {
      params['mes'] = searchParams.get('mes');
      params['anio'] = searchParams.get('anio');
    }
    if (state && state['fromAuthSearch']) {
      navigate(
        {
          pathname: state['fromPath'],
          search: searchParams.toString(),
        },
        {
          state,
        },
      );
    } else if (state && state['tarjeta']) {
      navigate(`${cardPath}?${searchParams.toString()}`, {
        state: {
          tarjeta: state['tarjeta'],
        },
      });
    } else if (state && state['fromAccountCardTotals']) {
      navigate({
        pathname: CARDS_TOTALS_BY_ACCOUNT_PATH.replace(':accountNumber', accountNumber),
        search: searchParams.toString(),
      });
    } else {
      navigate(HOME_PATH);
    }
  }, [state, navigate, searchParams, accountNumber]);

  useEffect(() => {
    let params = new AccountReportRequest();
    if (searchParams.has('mes') && searchParams.has('anio')) {
      params.mes = parseInt(searchParams.get('mes'));
      params.anio = parseInt(searchParams.get('anio'));
    }
    setAccountDataRequest(() =>
      coreService
        .getAccountData(accountNumber, params)
        .then((r) => {
          return r.body;
        })
        .catch((e) => {
          const message = new SearchAccountCardTotalsError(e.error).message;
          addNotification(message, 'error');
          goBack();
          return null;
        }),
    );
  }, [
    state,
    coreService,
    goBack,
    addNotification,
    navigate,
    searchParams,
    accountNumber,
    setAccountDataRequest,
  ]);

  const contextValue = {
    closeReport: () => goBack(),
    accountData: accountData.data,
    movimientosCuenta: accountData.data?.movimientos,
    searchParams,
  };
  return (
    <AccountDetailsContext.Provider value={contextValue}>
      <LoadingSpinner isLoading={accountData.isLoading}>{children}</LoadingSpinner>
    </AccountDetailsContext.Provider>
  );
};
